import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BookOpen, Check, ClipboardList, Clock, UserRound } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { api, errorMessage } from "../lib/api";
import { LookupForm } from "../components/home/LookupSection";
import { Reveal } from "../components/Reveal";

const STAGES = ["في الانتظار", "قيد الدراسة", "جاهز للنشر", "تم النشر"];
const fmt = (iso, lang) => iso ? new Date(iso).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—";

export default function MyPage() {
  const { code } = useParams();
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setData(null); setError("");
    if (!code) return;
    api.get(`/portal/${code}`).then((r) => setData(r.data)).catch((e) => setError(errorMessage(e, t.lookup.notFound)));
  }, [code, t]);

  if (!code || error) return (
    <main className="container" data-testid="my-page">
      <div className="page-head"><span className="kicker">{t.lookup.kicker}</span><h1>{t.lookup.title}</h1><p>{t.lookup.sub}</p></div>
      <div className="panel" style={{ maxWidth: 640, marginBottom: 80 }}><LookupForm initial={code || ""} error={error} /></div>
    </main>
  );
  if (!data) return <main className="page-loader"><span className="loader" /></main>;

  const { registration: r, opportunity: o } = data;
  const stageIdx = STAGES.indexOf(r.status);
  const facts = [[t.opps.journal, o?.journal_target], [t.opps.lead, o?.lead_researcher], [t.opps.deadline, o?.deadline], [t.opps.type, o?.study_type], [t.opps.stage, o?.stage]].filter(([, v]) => v);
  const fields = [["full_name", t.reg.name], ["specialty", t.reg.specialty], ["email", t.reg.email], ["phone", t.reg.phone], ["institution", t.reg.institution], ["country", t.reg.country], ["city", t.reg.city], ["orcid", t.reg.orcid]].filter(([k]) => r[k]);

  return (
    <main data-testid="my-page-result">
      <section className="detail-hero"><div className="container">
        <Reveal>
          <span className="kicker">{t.my.title}</span>
          <div className="detail-badges" style={{ marginTop: 12 }}><span className="badge badge-indigo">{r.specialty_label}</span><span className="badge badge-green" data-testid="my-status">{r.status}</span><span className="badge badge-muted en">{r.access_code}</span></div>
          <h1 data-testid="my-opportunity-title">{r.opportunity_title}</h1>
          <p className="ar">{t.my.sub} — {r.full_name}</p>
          <div className="stepper" style={{ maxWidth: 640 }}>{STAGES.map((s, i) => <div key={s} className={i < stageIdx ? "done" : i === stageIdx ? "now" : ""}>{s}</div>)}</div>
        </Reveal>
      </div></section>
      <div className="container detail-layout">
        <div className="detail-main">
          <Reveal className="panel"><h3><Clock size={20} />{t.my.timeline}</h3>
            <div className="timeline" data-testid="my-timeline">{[...(r.history || [])].reverse().map((h, i) => <div className={`tl-item ${i === 0 ? "current" : ""}`} key={h.at}><span className="tl-dot">{i === 0 && <Check size={12} />}</span><div><b>{h.status}</b>{h.note && <div className="tl-note">{h.note}</div>}<small>{fmt(h.at, lang)}</small></div></div>)}</div>
            <div className="facts"><div className="fact"><small>{t.my.registered}</small><b>{fmt(r.created_at, lang)}</b></div><div className="fact"><small>{t.my.approved}</small><b>{fmt(r.approved_at, lang)}</b></div></div>
            {r.admin_note && <p style={{ marginTop: 14 }}><b>{t.my.note}:</b> {r.admin_note}</p>}
          </Reveal>
          <Reveal className="panel" delay={0.05}><h3><UserRound size={20} />{t.my.data}</h3>{fields.map(([k, l]) => <div className="detail-row" key={k}><small>{l}</small><span>{r[k]}</span></div>)}</Reveal>
        </div>
        <aside>
          <Reveal className="panel" delay={0.1}><h3><BookOpen size={20} />{t.my.opp}</h3>
            {o ? <>
              <p className="en" style={{ fontWeight: 600, color: "var(--text)" }}>{o.title_en}</p>{o.title_ar && <p style={{ fontSize: 14 }}>{o.title_ar}</p>}
              {facts.length > 0 && <div className="facts">{facts.map(([k, v]) => <div className="fact" key={k}><small>{k}</small><b>{v}</b></div>)}</div>}
              {o.description && <p style={{ fontSize: 14 }}>{o.description}</p>}
              <Link to={`/opportunities/${o.id}`} className="btn btn-outline btn-sm" style={{ marginTop: 16 }} data-testid="my-open-opportunity"><ClipboardList size={15} />{t.my.open}</Link>
            </> : <p>{r.opportunity_title}</p>}
            <Link to="/my" className="link-btn" style={{ marginTop: 14, display: "inline-flex" }} data-testid="my-another-code">{t.my.another}</Link>
          </Reveal>
        </aside>
      </div>
    </main>
  );
}
